/**
 * Activity view — operational feed for OperationsManager / Executive.
 *
 * Surfaces:
 *   - User logins (so Tom sees Joel sign in)
 *   - User creation events
 *   - Recent calls (in/out, status, duration)
 *   - Kill-switch events
 *
 * Polls every 10s for freshness during demo. Will be replaced by Redis
 * Streams + WebSocket fan-out once Storm goes live.
 */

import { useCallback, useEffect, useState } from "react";
import { LogIn, UserPlus, Phone, Shield, RefreshCw } from "lucide-react";
import { api } from "../lib/api";
import { formatE164ForDisplay } from "../lib/phone";

type AuditItem = {
  kind: "audit";
  id: string;
  action: string;
  actor: { id: string; email: string; displayName: string | null } | null;
  resource: { id: string; email: string; displayName: string | null } | null;
  metadata: unknown;
  at: string;
};
type CallItem = {
  kind: "call";
  id: string;
  direction: string;
  from: string;
  to: string;
  status: string;
  durationSec: number | null;
  at: string;
};
type Item = AuditItem | CallItem;

const ACTION_ICON: Record<string, React.ComponentType<{ className?: string }>> = {
  "user.login": LogIn,
  "user.created": UserPlus,
  "killswitch.activate": Shield,
};

export function ActivityView() {
  const [items, setItems] = useState<Item[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api<{ items: Item[] }>("/api/activity");
      setItems(res.items);
      setError(null);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
    const t = setInterval(() => void load(), 10000);
    return () => clearInterval(t);
  }, [load]);

  return (
    <div className="pad-fluid">
      <div className="flex items-baseline justify-between mb-6">
        <h1 className="text-3xl font-bold text-rogue-900">Activity</h1>
        <button
          onClick={() => void load()}
          className="text-sm text-rogue-600 hover:text-rogue-800 flex items-center gap-1"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Refresh
        </button>
      </div>

      {error && (
        <div className="bg-red-50 text-red-700 rounded-lg pad-fluid mb-4 text-sm">{error}</div>
      )}

      <div className="bg-white rounded-xl shadow divide-y divide-rogue-100">
        {items.length === 0 && !loading && (
          <div className="pad-fluid text-rogue-500 text-center">No recent activity.</div>
        )}
        {items.map((it) => (
          <div key={`${it.kind}-${it.id}`} className="pad-fluid flex items-start gap-3">
            <ItemIcon item={it} />
            <div className="flex-1 min-w-0">
              <div className="text-sm text-rogue-900">{describe(it)}</div>
              <time className="text-xs text-rogue-500" dateTime={it.at}>
                {new Date(it.at).toLocaleString()}
              </time>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ItemIcon({ item }: { item: Item }): JSX.Element {
  if (item.kind === "audit") {
    const I = ACTION_ICON[item.action] ?? Shield;
    return <I className="w-5 h-5 text-rogue-700 shrink-0" />;
  }
  return <Phone className="w-5 h-5 text-emerald-700 shrink-0" />;
}

function describe(it: Item): string {
  if (it.kind === "audit") {
    const actor = it.actor?.displayName || it.actor?.email || "unknown";
    if (it.action === "user.login") return `${actor} signed in`;
    if (it.action === "user.created") {
      const target = it.resource?.displayName || it.resource?.email || "user";
      return `${target} was added`;
    }
    if (it.action === "killswitch.activate") return `Kill switch activated by ${actor}`;
    return `${actor}: ${it.action}`;
  }
  const from = formatE164ForDisplay(it.from);
  const to = formatE164ForDisplay(it.to);
  const dur = it.durationSec != null ? ` · ${it.durationSec}s` : "";
  if (it.direction === "inbound") return `Inbound from ${from} → ${to} · ${it.status}${dur}`;
  if (it.direction === "outbound") return `Outbound from ${from} → ${to} · ${it.status}${dur}`;
  return `Call ${from} → ${to} · ${it.status}${dur}`;
}
